import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  Play,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Clock,
  Key,
  ExternalLink,
  ShieldCheck,
  Trash2,
  Plus,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import AutocompleteInput from "../ui/AutocompleteInput";

/**
 * Smartsheet integration card for the Integrations page.
 *
 * Multi-sheet layout: token + N sheets, each with its own last-sync
 * stats and per-row Test/Sync/Rename/Delete actions. The "Hepsini
 * Senkronize Et" button hits legacy `POST /smartsheet/sync` which the
 * backend now interprets as "sync every enabled sheet" when
 * `state.sheets` is populated.
 */
export default function SmartsheetIntegrationSection({ isSuper }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Draft fields for the token + new-sheet form.
  const [tokenDraft, setTokenDraft] = useState("");
  const [newSheetLabel, setNewSheetLabel] = useState("");
  const [newSheetRef, setNewSheetRef] = useState("");
  const [newSheetTag, setNewSheetTag] = useState("");
  // iter50: sheet-kind picker + task-mode fields for the "add new sheet" form.
  const [newSheetKind, setNewSheetKind] = useState("deal");
  const [newTaskProject, setNewTaskProject] = useState("");
  const [newTaskList, setNewTaskList] = useState("");
  // Tenant projects + lists for the autocomplete dropdowns. Fetched once
  // when task mode is first shown so we don't hammer the backend.
  const [taskProjectOptions, setTaskProjectOptions] = useState([]); // [{name, id}]
  const [taskListOptions, setTaskListOptions] = useState([]); // [{name, project_id}]
  const [taskOptsLoaded, setTaskOptsLoaded] = useState(false);

  // Row-level busy flags keyed by sheet uuid → keeps buttons independent.
  const [busy, setBusy] = useState({});
  const [savingToken, setSavingToken] = useState(false);
  const [adding, setAdding] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [testing, setTesting] = useState(false);

  // Track which sheet is currently being renamed (inline edit).
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  const setSheetBusy = (id, flag) =>
    setBusy((prev) => ({ ...prev, [id]: flag }));

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/smartsheet/status");
      setStatus(data);
    } catch (e) {
      if (e.response?.status !== 403) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Durum okunamadı");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuper) refresh();
    else setLoading(false);
  }, [isSuper, refresh]);

  // Lazy-hydrate the autocomplete pool the first time the user picks
  // Task mode. We fetch tenant projects, then fan out per-project lists.
  const loadTaskOptions = useCallback(async () => {
    if (taskOptsLoaded) return;
    try {
      const { data: projs } = await api.get("/projects");
      setTaskProjectOptions((projs || []).map((p) => ({ id: p.id, name: p.name })));
      const listArrays = await Promise.all(
        (projs || []).map((p) =>
          api
            .get("/lists", { params: { project_id: p.id } })
            .then((r) => (r.data || []).map((l) => ({ name: l.name, project_id: p.id })))
            .catch(() => []),
        ),
      );
      setTaskListOptions(listArrays.flat());
      setTaskOptsLoaded(true);
    } catch {
      // Non-fatal — user can still type free-form values.
    }
  }, [taskOptsLoaded]);

  useEffect(() => {
    if (isSuper && newSheetKind === "task" && !taskOptsLoaded) {
      loadTaskOptions();
    }
  }, [isSuper, newSheetKind, taskOptsLoaded, loadTaskOptions]);

  // Derived: list names scoped to the typed project when it matches an
  // existing project. When no match, fall back to all unique list names
  // in the tenant so the user still gets prefix-match suggestions.
  const projectListOptions = useMemo(() => {
    const typed = (newTaskProject || "").trim().toLocaleLowerCase("tr");
    const matched = taskProjectOptions.find(
      (p) => p.name.toLocaleLowerCase("tr") === typed,
    );
    const scoped = matched
      ? taskListOptions.filter((l) => l.project_id === matched.id)
      : taskListOptions;
    return Array.from(new Set(scoped.map((l) => l.name)));
  }, [newTaskProject, taskProjectOptions, taskListOptions]);

  if (!isSuper) return null;

  const state = status?.state || {};
  const sheets = state.sheets || [];

  // ── Token actions ───────────────────────────────────────────────
  const saveToken = async () => {
    const t = tokenDraft.trim();
    if (t && t.length < 12) {
      toast.error("Token çok kısa görünüyor — Smartsheet token'ları ~30+ karakterdir.");
      return;
    }
    setSavingToken(true);
    try {
      await api.put("/smartsheet/config", { api_token: t });
      toast.success(t ? "Token kaydedildi" : "Token temizlendi");
      setTokenDraft("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Token kaydedilemedi");
    } finally {
      setSavingToken(false);
    }
  };

  const clearToken = async () => {
    if (!window.confirm("Kayıtlı Smartsheet token silinsin mi?")) return;
    setSavingToken(true);
    try {
      await api.put("/smartsheet/config", { api_token: "" });
      toast.success("Token temizlendi");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingToken(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/smartsheet/test-connection");
      toast.success(
        `✓ Bağlantı OK · ${data.account_email || data.account_name || "hesap doğrulandı"}`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bağlantı başarısız");
    } finally {
      setTesting(false);
    }
  };

  // ── Sheet actions ───────────────────────────────────────────────
  const addSheet = async () => {
    const label = newSheetLabel.trim();
    const ref = newSheetRef.trim();
    const tag = newSheetTag.trim();
    if (!label || !ref) {
      toast.error("Etiket ve sheet URL/ID boş bırakılamaz");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        label,
        sheet_ref: ref,
        enabled: true,
        import_tag: tag || null,
        kind: newSheetKind,
      };
      if (newSheetKind === "task") {
        // Empty strings → backend derives from sheet name + "Genel Check List".
        payload.task_project_name = newTaskProject.trim() || null;
        payload.task_list_name = newTaskList.trim() || null;
      }
      await api.post("/smartsheet/sheets", payload);
      toast.success(`"${label}" eklendi`);
      setNewSheetLabel("");
      setNewSheetRef("");
      setNewSheetTag("");
      setNewSheetKind("deal");
      setNewTaskProject("");
      setNewTaskList("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eklenemedi");
    } finally {
      setAdding(false);
    }
  };

  const renameSheet = async (sheet) => {
    const label = editingLabel.trim();
    if (!label || label === sheet.label) {
      setEditingId(null);
      return;
    }
    setSheetBusy(sheet.id, true);
    try {
      await api.patch(`/smartsheet/sheets/${sheet.id}`, { label });
      toast.success("Etiket güncellendi");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSheetBusy(sheet.id, false);
    }
  };

  const toggleSheetEnabled = async (sheet, next) => {
    setSheetBusy(sheet.id, true);
    try {
      await api.patch(`/smartsheet/sheets/${sheet.id}`, { enabled: next });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSheetBusy(sheet.id, false);
    }
  };

  const updateImportTag = async (sheet, nextTag) => {
    setSheetBusy(sheet.id, true);
    try {
      await api.patch(`/smartsheet/sheets/${sheet.id}`, { import_tag: nextTag });
      toast.success(nextTag ? `Tag "${nextTag}" kaydedildi` : "Tag temizlendi");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSheetBusy(sheet.id, false);
    }
  };

  const deleteSheet = async (sheet) => {
    if (!window.confirm(`"${sheet.label}" listeden çıkarılsın mı? (İçe aktarılmış deal'lar Serpix'te kalır.)`)) return;
    setSheetBusy(sheet.id, true);
    try {
      await api.delete(`/smartsheet/sheets/${sheet.id}`);
      toast.success("Sheet çıkarıldı");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSheetBusy(sheet.id, false);
    }
  };

  const syncOne = async (sheet) => {
    if (!window.confirm(`"${sheet.label}" Smartsheet'ten Serpix'e çekilecek. Devam?`)) return;
    setSheetBusy(sheet.id, true);
    try {
      const { data } = await api.post(`/smartsheet/sheets/${sheet.id}/sync`);
      const r = data.report || {};
      toast.success(
        `${sheet.label}: ${r.deals_created ?? 0} yeni · ${r.deals_updated ?? 0} güncellenen`
      );
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Senkron başarısız");
    } finally {
      setSheetBusy(sheet.id, false);
    }
  };

  const syncAll = async () => {
    const enabled = sheets.filter((s) => s.enabled).length;
    if (enabled === 0) {
      toast.warning("Aktif hiçbir sheet yok");
      return;
    }
    if (!window.confirm(`${enabled} aktif sheet Serpix'e çekilecek. Devam?`)) return;
    setSyncingAll(true);
    try {
      const { data } = await api.post("/smartsheet/sync", {});
      const r = data.report || {};
      toast.success(
        `Toplam ${r.sheets?.length ?? 0} sheet · ${r.deals_created ?? 0} yeni · ${r.deals_updated ?? 0} güncellenen`
      );
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Senkron başarısız");
    } finally {
      setSyncingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-6">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white p-6"
      data-testid="smartsheet-integration-section"
    >
      {/* Header */}
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-emerald-50 to-sky-50">
          <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-semibold">Smartsheet</h3>
            <span
              data-testid="smartsheet-readonly-badge"
              title="Serpix Smartsheet'e asla yazmaz — sadece okuma yapar."
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200"
            >
              <ShieldCheck className="h-3 w-3" />
              READ-ONLY · Smartsheet → Serpix
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Birden fazla sheet ekleyip Serpix Deals modülüne aktarın.{" "}
            <span className="text-zinc-400">
              Serpix asla Smartsheet&apos;e yazmaz, silmez, güncellemez.
            </span>
          </p>
        </div>
      </div>

      {/* Read-only policy call-out */}
      <div className="mb-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[11px] text-emerald-900">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <b>Veri koruma garantisi:</b> Bağlantı tek yönlüdür. Serpix
          Smartsheet&apos;ten yalnızca <b>GET</b> istekleri yapar — <b>POST/PUT/PATCH/DELETE</b> gönderimleri
          kod düzeyinde <code className="rounded bg-emerald-100 px-1">SmartsheetWriteForbidden</code>{" "}
          ile engellenir. Sheet&apos;lerinizdeki hiçbir veri değişmez.
        </span>
      </div>

      {/* Token block */}
      <div className="mb-5 space-y-2">
        <Label className="text-xs uppercase tracking-wider text-zinc-500">
          <Key className="mr-1 inline h-3 w-3" />
          Smartsheet API Access Token
        </Label>
        {state.api_token_set ? (
          <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-200">
              <CheckCircle2 className="h-3 w-3" />
              Kayıtlı
            </span>
            <code
              data-testid="smartsheet-token-masked"
              className="flex-1 font-mono text-xs tracking-wider text-zinc-700"
            >
              {state.api_token_masked}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={clearToken}
              disabled={savingToken}
              data-testid="smartsheet-token-clear"
              className="h-8 text-xs text-red-700 hover:bg-red-50"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Sil
            </Button>
          </div>
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            Bu tenant için Smartsheet token&apos;ı henüz eklenmemiş.
            {status?.token_configured && (
              <> Backend&apos;in ortak env token&apos;ı kullanılıyor — kendi hesabınıza geçmek için aşağıya kendi token&apos;ınızı girin.</>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            type="password"
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            placeholder={state.api_token_set ? "Yeni token gir (üzerine yazılacak)" : "sh_xxxxxxxxxxxxxxxxxxxxxxxxx"}
            className="flex-1 font-mono text-xs"
            disabled={savingToken}
            data-testid="smartsheet-token-input"
          />
          <Button
            onClick={saveToken}
            disabled={savingToken || !tokenDraft.trim()}
            data-testid="smartsheet-token-save"
            className="bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {savingToken ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
          </Button>
          <Button
            variant="outline"
            onClick={testConnection}
            disabled={testing || !status?.token_configured}
            data-testid="smartsheet-test-btn"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500">
          Smartsheet → Sağ üst avatar → <b>Personal Settings</b> → <b>API Access</b> →{" "}
          <b>Generate new access token</b>.
          <a
            href="https://smartsheet.redoc.ly/#section/API-Basics/Authentication"
            target="_blank"
            rel="noreferrer"
            className="ml-1 inline-flex items-center gap-0.5 text-emerald-700 hover:underline"
          >
            Dokümantasyon <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      {/* Sheets list */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            Bağlı Sheet&apos;ler{" "}
            <span className="ml-1 tabular-nums text-zinc-400">({sheets.length})</span>
          </Label>
          <p className="mt-0.5 text-[11px] text-zinc-500">
            Toplam <b>{status?.imported_deals ?? 0}</b> deal içe aktarıldı
          </p>
        </div>
        <Button
          onClick={syncAll}
          disabled={syncingAll || !status?.token_configured || sheets.filter((s) => s.enabled).length === 0}
          data-testid="smartsheet-sync-all-btn"
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          {syncingAll ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Senkronize ediliyor…
            </>
          ) : (
            <>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Hepsini Senkronize Et
            </>
          )}
        </Button>
      </div>

      <div className="mb-4 space-y-2" data-testid="smartsheet-sheets-list">
        {sheets.length === 0 && (
          <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
            Henüz sheet eklenmemiş. Aşağıdan ilk sheet&apos;ini ekle.
          </div>
        )}
        {sheets.map((s) => (
          <SheetRow
            key={s.id}
            sheet={s}
            busy={!!busy[s.id]}
            editing={editingId === s.id}
            editingLabel={editingLabel}
            setEditingLabel={setEditingLabel}
            startEdit={() => {
              setEditingId(s.id);
              setEditingLabel(s.label);
            }}
            cancelEdit={() => setEditingId(null)}
            confirmEdit={() => renameSheet(s)}
            onToggle={(v) => toggleSheetEnabled(s, v)}
            onSync={() => syncOne(s)}
            onDelete={() => deleteSheet(s)}
            onImportTagChange={(t) => updateImportTag(s, t)}
          />
        ))}
      </div>

      {/* Add-new form */}
      <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/50 p-3">
        <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          <Plus className="mr-0.5 inline h-3 w-3" />
          Yeni sheet ekle
        </Label>

        {/* iter50: Kind selector — deal (default) vs task */}
        <div className="mt-2 flex items-center gap-1 text-[11px] font-medium">
          <span className="text-zinc-500 mr-1">Tür:</span>
          <button
            type="button"
            onClick={() => setNewSheetKind("deal")}
            data-testid="smartsheet-new-sheet-kind-deal"
            className={[
              "rounded-full px-2.5 py-0.5 transition-colors",
              newSheetKind === "deal"
                ? "bg-zinc-900 text-white"
                : "bg-white text-zinc-600 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-50",
            ].join(" ")}
          >
            Deal Pipeline
          </button>
          <button
            type="button"
            onClick={() => setNewSheetKind("task")}
            data-testid="smartsheet-new-sheet-kind-task"
            className={[
              "rounded-full px-2.5 py-0.5 transition-colors",
              newSheetKind === "task"
                ? "bg-emerald-600 text-white"
                : "bg-white text-zinc-600 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-50",
            ].join(" ")}
          >
            Task List
          </button>
          <span className="ml-2 text-[10px] text-zinc-400">
            {newSheetKind === "deal"
              ? "→ Satış Pipeline / Deals'a import edilir"
              : "→ Görevlere import edilir (Proje ▸ Grup ▸ Task)"}
          </span>
        </div>

        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_2fr_1fr_auto]">
          <Input
            placeholder="Etiket (örn: Sales Pipeline)"
            value={newSheetLabel}
            onChange={(e) => setNewSheetLabel(e.target.value)}
            disabled={adding}
            data-testid="smartsheet-new-sheet-label"
          />
          <Input
            placeholder="Sheet URL veya numeric id"
            value={newSheetRef}
            onChange={(e) => setNewSheetRef(e.target.value)}
            disabled={adding}
            data-testid="smartsheet-new-sheet-ref"
            className="font-mono text-xs"
          />
          <Input
            placeholder={newSheetKind === "task" ? "Tag (kaynak) — opsiyonel" : "Tag (opsiyonel)"}
            value={newSheetTag}
            onChange={(e) => setNewSheetTag(e.target.value)}
            disabled={adding}
            data-testid="smartsheet-new-sheet-tag"
          />
          <Button
            onClick={addSheet}
            disabled={adding || !newSheetLabel.trim() || !newSheetRef.trim()}
            data-testid="smartsheet-new-sheet-add"
            className="bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Ekle</>}
          </Button>
        </div>

        {/* Task-mode: Project + List name inputs. Both optional — backend
            falls back to sheet name + "Genel Check List". Autocomplete
            surfaces existing projects & lists so users don't accidentally
            spawn duplicates. */}
        {newSheetKind === "task" && (
          <div
            className="mt-2 grid gap-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-2 sm:grid-cols-2"
            data-testid="smartsheet-new-sheet-task-fields"
          >
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                Proje adı
              </label>
              <AutocompleteInput
                value={newTaskProject}
                onChange={setNewTaskProject}
                options={taskProjectOptions.map((p) => p.name)}
                placeholder="(boş → sheet adı kullanılır)"
                disabled={adding}
                testId="smartsheet-new-sheet-project"
                className="mt-1 h-8 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                Çalışma grubu (List) adı
              </label>
              <AutocompleteInput
                value={newTaskList}
                onChange={setNewTaskList}
                options={projectListOptions}
                placeholder="(boş → “Genel Check List”)"
                disabled={adding}
                testId="smartsheet-new-sheet-list"
                className="mt-1 h-8 text-xs"
              />
            </div>
          </div>
        )}

        <p className="mt-1.5 text-[11px] text-zinc-500">
          {newSheetKind === "task" ? (
            <>
              <b>Task modu</b> — sheet satırları görev olarak import edilir. Proje
              ve grup boş bırakılırsa sheet adı + &quot;Genel Check List&quot; kullanılır.
              Tekrar sync&apos;te aynı satırlar güncellenir (idempotent).
            </>
          ) : (
            <>
              Tag opsiyoneldir — bu sheet&apos;ten import edilen her deal&apos;in
              etiketine eklenir; Kanban filtresinde &quot;kaynak&quot; ayrımı için kullanılır.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function SheetRow({
  sheet,
  busy,
  editing,
  editingLabel,
  setEditingLabel,
  startEdit,
  cancelEdit,
  confirmEdit,
  onToggle,
  onSync,
  onDelete,
  onImportTagChange,
}) {
  const r = sheet.last_sync_report;
  const hasError = !!sheet.last_error;
  const [tagDraft, setTagDraft] = useState(sheet.import_tag || "");
  const [tagFocused, setTagFocused] = useState(false);
  // Keep the draft in sync with server-side updates from other tabs.
  useEffect(() => {
    if (!tagFocused) setTagDraft(sheet.import_tag || "");
  }, [sheet.import_tag, tagFocused]);
  const commitTag = () => {
    setTagFocused(false);
    const next = tagDraft.trim();
    if (next === (sheet.import_tag || "")) return;
    onImportTagChange(next);
  };
  return (
    <div
      className={`rounded-md border p-3 transition-colors ${
        hasError
          ? "border-rose-200 bg-rose-50/40"
          : sheet.enabled
          ? "border-zinc-200 bg-white"
          : "border-zinc-200 bg-zinc-50/50 opacity-70"
      }`}
      data-testid={`smartsheet-sheet-row-${sheet.id}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1">
              <Input
                value={editingLabel}
                onChange={(e) => setEditingLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmEdit();
                  if (e.key === "Escape") cancelEdit();
                }}
                autoFocus
                className="h-7 text-sm"
                data-testid={`smartsheet-sheet-rename-input-${sheet.id}`}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={confirmEdit}
                disabled={busy}
                className="h-7 px-2 text-emerald-700"
                data-testid={`smartsheet-sheet-rename-save-${sheet.id}`}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEdit}
                className="h-7 px-2 text-zinc-500"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-zinc-900">
                {sheet.label}
              </span>
              <button
                onClick={startEdit}
                className="text-zinc-400 hover:text-zinc-700"
                aria-label="Etiketi değiştir"
                data-testid={`smartsheet-sheet-rename-btn-${sheet.id}`}
              >
                <Pencil className="h-3 w-3" />
              </button>
              {/* iter50: kind badge — deal (violet) or task (emerald). */}
              {sheet.kind === "task" ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800"
                  data-testid={`smartsheet-sheet-kind-badge-${sheet.id}`}
                  title={
                    sheet.task_project_name || sheet.task_list_name
                      ? `${sheet.task_project_name || "(sheet adı)"} ▸ ${sheet.task_list_name || "Genel Check List"}`
                      : "Task modunda import edilir"
                  }
                >
                  ✅ Task
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800"
                  data-testid={`smartsheet-sheet-kind-badge-${sheet.id}`}
                  title="Deal olarak import edilir"
                >
                  🎯 Deal
                </span>
              )}
              {sheet.sheet_name && sheet.sheet_name !== sheet.label && (
                <span className="text-[11px] text-zinc-500">· {sheet.sheet_name}</span>
              )}
            </div>
          )}
          <div className="mt-0.5 font-mono text-[11px] text-zinc-500 truncate">
            {sheet.sheet_ref}
          </div>
          {/* Import tag — appended to every deal.tags on sync so Kanban
              can filter by "which sheet did this come from". */}
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Tag:
            </span>
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onFocus={() => setTagFocused(true)}
              onBlur={commitTag}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
              disabled={busy}
              placeholder="ör: pipeline"
              className="h-6 max-w-[160px] text-[11px]"
              data-testid={`smartsheet-sheet-tag-input-${sheet.id}`}
            />
            {sheet.import_tag && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-800">
                {sheet.import_tag}
              </span>
            )}
          </div>
        </div>
        <Switch
          checked={!!sheet.enabled}
          onCheckedChange={onToggle}
          disabled={busy}
          data-testid={`smartsheet-sheet-toggle-${sheet.id}`}
        />
      </div>

      {(sheet.last_sync_at || r) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-600">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3 text-zinc-400" />
            {sheet.last_sync_at
              ? new Date(sheet.last_sync_at).toLocaleString("tr-TR")
              : "Hiç sync edilmedi"}
          </span>
          {r && (
            <>
              <span className="tabular-nums">
                Yeni: <b>{sheet.kind === "task" ? (r.tasks_created ?? 0) : (r.deals_created ?? 0)}</b>
              </span>
              <span className="tabular-nums">
                Güncellenen: <b>{sheet.kind === "task" ? (r.tasks_updated ?? 0) : (r.deals_updated ?? 0)}</b>
              </span>
              <span className="tabular-nums">Satır: <b>{r.total_rows ?? 0}</b></span>
            </>
          )}
        </div>
      )}

      {hasError && (
        <div className="mt-2 flex items-start gap-1.5 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="font-mono">{sheet.last_error}</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap justify-end gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={onSync}
          disabled={busy || !sheet.enabled}
          data-testid={`smartsheet-sheet-sync-${sheet.id}`}
          className="h-7 text-xs"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <RefreshCw className="mr-1 h-3 w-3" />
              Senkronize
            </>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={busy}
          data-testid={`smartsheet-sheet-delete-${sheet.id}`}
          className="h-7 text-xs text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
